import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    Tooltip,
    Divider,
    Skeleton,
    Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NoteIcon from '@mui/icons-material/Note';
import NoteEditor from './NoteEditor';
import { Note, noteService, CreateNoteData, UpdateNoteData } from '../../services/noteService';
import { useTranslation } from '../../i18n';

interface NoteSidebarProps {
    courseId: number;
    lessonId?: number;
    lessonTitle?: string;
}

const NoteSidebar: React.FC<NoteSidebarProps> = ({
    courseId,
    lessonId,
    lessonTitle,
}) => {
    const { t } = useTranslation();
    const [lessonNotes, setLessonNotes] = useState<Note[]>([]);
    const [courseNotes, setCourseNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(true);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [showCourseNotes, setShowCourseNotes] = useState(false);

    useEffect(() => {
        loadNotes();
    }, [courseId, lessonId]);

    const loadNotes = async () => {
        try {
            setLoading(true);

            // Load lesson notes if lessonId is provided
            if (lessonId) {
                const lNotes = await noteService.getNotesByLesson(lessonId);
                setLessonNotes(lNotes);
            }

            // Load all course notes
            const cNotes = await noteService.getNotesByCourse(courseId);
            // Filter out lesson notes to avoid duplicates
            const filteredCourseNotes = lessonId
                ? cNotes.filter((n) => n.lesson?.id !== lessonId)
                : cNotes;
            setCourseNotes(filteredCourseNotes);
        } catch (err) {
            console.error('Error loading notes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingNote(null);
        setEditorOpen(true);
    };

    const handleEdit = (note: Note) => {
        setEditingNote(note);
        setEditorOpen(true);
    };

    const handleDelete = async (note: Note) => {
        if (!window.confirm(t('notes.deleteConfirmMessage'))) return;

        try {
            await noteService.deleteNote(note.id);
            if (note.lesson?.id === lessonId) {
                setLessonNotes(lessonNotes.filter((n) => n.id !== note.id));
            } else {
                setCourseNotes(courseNotes.filter((n) => n.id !== note.id));
            }
        } catch (err) {
            console.error('Error deleting note:', err);
        }
    };

    const handleSave = async (data: CreateNoteData | UpdateNoteData) => {
        if (editingNote) {
            const updated = await noteService.updateNote(editingNote.id, data as UpdateNoteData);
            if (editingNote.lesson?.id === lessonId) {
                setLessonNotes(lessonNotes.map((n) => (n.id === editingNote.id ? updated : n)));
            } else {
                setCourseNotes(courseNotes.map((n) => (n.id === editingNote.id ? updated : n)));
            }
        } else {
            const created = await noteService.createNote(data as CreateNoteData);
            if (created.lesson?.id === lessonId) {
                setLessonNotes([created, ...lessonNotes]);
            } else {
                setCourseNotes([created, ...courseNotes]);
            }
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        });
    };

    const renderNote = (note: Note) => (
        <ListItem
            key={note.id}
            sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderLeft: note.color ? `3px solid ${note.color}` : undefined,
                pl: note.color ? 1.5 : 2,
                py: 1,
            }}
            secondaryAction={
                <Box>
                    <Tooltip title={t('notes.edit')}>
                        <IconButton size="small" onClick={() => handleEdit(note)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('notes.delete')}>
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(note)}
                            color="error"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            }
        >
            <ListItemText
                primary={note.title || t('notes.untitled')}
                secondary={
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            pr: 6,
                        }}
                    >
                        {note.content}
                    </Typography>
                }
                primaryTypographyProps={{
                    variant: 'subtitle2',
                    fontWeight: 500,
                }}
            />
            <Typography variant="caption" color="text.secondary">
                {formatDate(note.updatedAt)}
            </Typography>
        </ListItem>
    );

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="rounded" height={80} sx={{ mt: 1 }} />
                <Skeleton variant="rounded" height={80} sx={{ mt: 1 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t('notes.notes')}
                </Typography>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    {t('notes.add')}
                </Button>
            </Box>

            <Divider />

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {/* Lesson Notes Section */}
                {lessonId && (
                    <>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.5, display: 'block' }}
                        >
                            {lessonTitle || t('notes.currentLesson')}
                        </Typography>

                        {lessonNotes.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <NoteIcon sx={{ fontSize: 32, opacity: 0.3, mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('notes.noLessonNotes')}
                                </Typography>
                            </Box>
                        ) : (
                            <List dense disablePadding>
                                {lessonNotes.map(renderNote)}
                            </List>
                        )}

                        <Divider sx={{ my: 1 }} />
                    </>
                )}

                {/* Course Notes Section (Collapsible) */}
                {courseNotes.length > 0 && (
                    <>
                        <ListItemButton
                            onClick={() => setShowCourseNotes(!showCourseNotes)}
                            sx={{ py: 0.5 }}
                        >
                            <ListItemText
                                primary={
                                    <Typography variant="caption" color="text.secondary">
                                        {t('notes.otherCourseNotes')} ({courseNotes.length})
                                    </Typography>
                                }
                            />
                            {showCourseNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemButton>

                        <Collapse in={showCourseNotes}>
                            <List dense disablePadding>
                                {courseNotes.map(renderNote)}
                            </List>
                        </Collapse>
                    </>
                )}
            </Box>

            <NoteEditor
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onSave={handleSave}
                note={editingNote}
                courseId={courseId}
                lessonId={lessonId}
            />
        </Box>
    );
};

export default NoteSidebar;
